import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Printer, Wallet, Ban } from 'lucide-react';
import * as facturacion from '@/api/facturacion.api';
import type { EstadoDeFactura } from '@/api/facturacion.api';
import { AVISO_DE_APROBACION, quedoEnAprobaciones } from '@/api/aprobaciones.api';
import { EntityLink } from '@/components/datos/EntityLink';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { dinero, dineroCent, fecha, fechaDia } from '@/lib/formato-tienda';

/**
 * LA FACTURA — el documento, como se abre en una pestaña.
 *
 * Todo lo que se lee aquí viene CONGELADO de la factura (renglones, cliente,
 * emisor): no se vuelve a pedir el producto ni la clienta, porque un papel que
 * cambia cuando alguien edita el catálogo ya no es el que se entregó.
 *
 * «Imprimir / PDF» es la impresión del navegador: ahí mismo se elige «Guardar
 * como PDF». Su recorte de impresión es el de `#factura-para-imprimir` en `index.css`.
 */

const ESTADO: Record<EstadoDeFactura, { etiqueta: string; variant: 'warning' | 'success' | 'danger' }> = {
  PENDIENTE: { etiqueta: 'Pendiente de pago', variant: 'warning' },
  VENCIDA: { etiqueta: 'Vencida', variant: 'danger' },
  PAGADA: { etiqueta: 'Pagada', variant: 'success' },
  ANULADA: { etiqueta: 'Anulada', variant: 'danger' },
};

export function DetalleDeFactura({ id }: { id: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [anulando, setAnulando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [constancia, setConstancia] = useState<string | null>(null);

  const { data: f, isLoading } = useQuery({
    queryKey: ['factura', id],
    queryFn: () => facturacion.leerFactura(id),
  });

  const invalidar = () => {
    for (const k of ['factura', 'facturas', 'ventas', 'venta', 'inventario', 'productos', 'tienda']) {
      void qc.invalidateQueries({ queryKey: [k] });
    }
  };

  const pagar = useMutation({
    mutationFn: () => facturacion.pagarFactura(id),
    onSuccess: () => {
      toast.success('Factura marcada como pagada.');
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  const anular = useMutation({
    mutationFn: () => facturacion.anularFactura(id, motivo.trim()),
    onSuccess: () => {
      toast.success('Factura anulada. La existencia volvió al inventario.');
      setAnulando(false);
      setMotivo('');
      invalidar();
    },
    onError: (e) => {
      if (quedoEnAprobaciones(e)) {
        toast.info(AVISO_DE_APROBACION, e.message);
        setAnulando(false);
        setMotivo('');
        return;
      }
      toast.error(e instanceof Error ? e.message : 'No se pudo anular');
    },
  });

  const anotar = useMutation({
    mutationFn: () => facturacion.anotarConstancia(id, (constancia ?? '').trim() || null),
    onSuccess: () => {
      toast.success('Constancia anotada.');
      setConstancia(null);
      void qc.invalidateQueries({ queryKey: ['factura'] });
      void qc.invalidateQueries({ queryKey: ['facturacion'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo anotar'),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }
  if (!f) return <p className="text-sm text-slate-500">No se encontró la factura.</p>;

  const piezas = f.lineasJson.reduce((a, l) => a + l.cantidad, 0);
  const e = f.emisorJson;
  const porPagar = f.estado === 'PENDIENTE' || f.estado === 'VENCIDA';
  const retenido = f.retItbisCent + f.retIsrCent;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge variant={ESTADO[f.estado].variant}>{ESTADO[f.estado].etiqueta}</Badge>
        <div className="flex flex-wrap gap-1.5">
          {porPagar && (
            <Button size="sm" onClick={() => pagar.mutate()} disabled={pagar.isPending}>
              <Wallet className="h-3.5 w-3.5" />
              Marcar pagada
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => window.print()}>
            <Printer className="h-3.5 w-3.5" />
            Imprimir / PDF
          </Button>
          {f.estado !== 'ANULADA' && !anulando && (
            <Button size="sm" variant="ghost" onClick={() => setAnulando(true)}>
              <Ban className="h-3.5 w-3.5" />
              Anular
            </Button>
          )}
        </div>
      </div>

      {anulando && (
        <div className="space-y-2 rounded-lg border border-surface-border p-3">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Anular la factura anula también su venta: {piezas === 1 ? 'la pieza vuelve' : `las ${piezas} piezas vuelven`}{' '}
            al inventario. La factura no se borra; queda anulada con su número.
          </p>
          <Input
            label="Motivo"
            value={motivo}
            onChange={(ev) => setMotivo(ev.target.value)}
            placeholder="La clienta se arrepintió"
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setAnulando(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => anular.mutate()}
              disabled={!motivo.trim() || anular.isPending}
            >
              {anular.isPending ? 'Anulando…' : 'Anular factura'}
            </Button>
          </div>
        </div>
      )}

      {/* Lo que se imprime. El `id` es el que aísla el CSS de impresión. */}
      <div
        id="factura-para-imprimir"
        className="rounded-lg border border-surface-border bg-surface p-5 sm:p-7"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p
              className="text-xl font-semibold text-slate-900 dark:text-slate-100"
              style={{ fontFamily: 'var(--f-titulos, inherit)', letterSpacing: 'var(--f-titulos-espacio, normal)' }}
            >
              {e.nombre || 'Tu tienda'}
            </p>
            {e.rnc && <p className="text-xs text-slate-500">RNC {e.rnc}</p>}
            {e.direccion && <p className="text-xs text-slate-500">{e.direccion}</p>}
            {e.telefono && <p className="text-xs text-slate-500">{e.telefono}</p>}
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Factura</p>
            <p className="font-mono text-base text-slate-900 dark:text-slate-100">{f.numero}</p>
            <p className="text-xs text-slate-500">{fecha(f.emitidaAt, true)}</p>
            {f.ncf && (
              <p className="mt-1 text-xs text-slate-700 dark:text-slate-200">
                NCF <span className="font-mono">{f.ncf}</span>
                {f.ncfVence && <span className="text-slate-500"> · vence {fechaDia(f.ncfVence)}</span>}
              </p>
            )}
            {porPagar && f.vence && (
              <p className="mt-1 text-xs text-slate-500">Se paga hasta el {fechaDia(f.vence)}</p>
            )}
            {f.despachoDesde && (
              <p className="mt-1 text-xs text-slate-500">Se despacha desde {f.despachoDesde}</p>
            )}
            {f.estado === 'ANULADA' && (
              <p className="mt-1 text-sm font-semibold uppercase text-red-600">Anulada</p>
            )}
          </div>
        </div>

        <div className="mt-4 border-t border-surface-border pt-3 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Cliente</p>
          <p className="text-slate-900 dark:text-slate-100">{f.clienteNombre}</p>
          <p className="text-xs text-slate-500">
            {[
              f.clienteDocumento && `RNC/Cédula ${f.clienteDocumento}`,
              f.clienteTelefono,
              f.clienteDireccion,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>

        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-1.5 pr-2 font-medium">Cant.</th>
              <th className="py-1.5 pr-2 font-medium">Descripción</th>
              <th className="py-1.5 pr-2 text-right font-medium">Precio</th>
              <th className="py-1.5 text-right font-medium">Importe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {f.lineasJson.map((l, i) => (
              <tr key={i}>
                <td className="py-1.5 pr-2 align-top tabular-nums text-slate-500">{l.cantidad}</td>
                <td className="py-1.5 pr-2 text-slate-900 dark:text-slate-100">
                  {l.sku && <span className="font-mono text-xs text-slate-500">{l.sku} </span>}
                  {l.descripcion}
                  <span className="text-slate-500"> · {l.talla}</span>
                  {l.descuento > 0 && (
                    <span className="block text-xs text-slate-500">Rebaja −{dinero(l.descuento)}</span>
                  )}
                </td>
                <td className="py-1.5 pr-2 text-right align-top tabular-nums text-slate-600 dark:text-slate-300">
                  {dinero(l.precio)}
                </td>
                <td className="py-1.5 text-right align-top tabular-nums text-slate-900 dark:text-slate-100">
                  {dinero(l.importe)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="ml-auto mt-3 w-full max-w-xs space-y-0.5 border-t-2 border-surface-border pt-2 text-sm">
          <Renglon etiqueta="Subtotal" valor={dinero(f.subtotal)} />
          {f.descuento > 0 && <Renglon etiqueta="Descuento" valor={`−${dinero(f.descuento)}`} />}
          {f.envio > 0 && <Renglon etiqueta="Envío" valor={dinero(f.envio)} />}
          {f.itbisTasa > 0 && (
            <>
              <Renglon etiqueta="Base gravada" valor={dineroCent(f.montoCent - f.exentoCent)} />
              {f.exentoCent > 0 && <Renglon etiqueta="Exento" valor={dineroCent(f.exentoCent)} />}
              <Renglon etiqueta={`ITBIS ${f.itbisTasa}% (incluido)`} valor={dineroCent(f.itbisCent)} />
            </>
          )}
          <div className="flex items-baseline justify-between pt-1 text-base font-semibold text-slate-900 dark:text-slate-100">
            <span>TOTAL</span>
            <span className="tabular-nums">{dinero(f.total)}</span>
          </div>
          {retenido > 0 && (
            <div className="mt-2 space-y-0.5 rounded-md border border-surface-border p-2">
              {f.retItbisCent > 0 && (
                <Renglon etiqueta="ITBIS retenido por el cliente" valor={`−${dineroCent(f.retItbisCent)}`} />
              )}
              {f.retIsrCent > 0 && (
                <Renglon etiqueta={`ISR ${f.retIsrTasa}% retenido por el cliente`} valor={`−${dineroCent(f.retIsrCent)}`} />
              )}
              <div className="flex items-baseline justify-between font-semibold text-slate-900 dark:text-slate-100">
                <span>Neto a recibir</span>
                <span className="tabular-nums">{dineroCent(f.netoCent)}</span>
              </div>
              <p className="text-[11px] text-slate-500">
                El cliente deposita lo retenido a la DGII y entrega su constancia de retención.
              </p>
            </div>
          )}
        </div>

        <p className="mt-3 text-xs text-slate-500">
          {piezas} pieza{piezas === 1 ? '' : 's'}
          {f.sale.formaDePago && ` · ${f.sale.formaDePago}`}
          {f.estado === 'PAGADA' && ' · pagada'}
          {porPagar && ' · pendiente de pago'}
        </p>
        {f.devuelto > 0 && (
          <p className="mt-1 text-xs text-slate-500">
            Devuelto después de emitida: {dinero(f.devuelto)}.
          </p>
        )}
        {f.notas && <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">{f.notas}</p>}
        {e.pie && <p className="mt-2 whitespace-pre-line text-xs text-slate-500">{e.pie}</p>}

        {/* Sin NCF no vale ante la DGII, y un papel que no lo dice se usa para declarar. */}
        {!f.ncf && (
          <p className="mt-4 border-t border-surface-border pt-2 text-center text-[11px] text-slate-400">
            Factura sin valor de comprobante fiscal (sin NCF).
          </p>
        )}
      </div>

      {retenido > 0 && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-surface-border p-3">
          <div className="min-w-0 flex-1">
            <Input
              label="Constancia de retención"
              value={constancia ?? f.retConstancia ?? ''}
              onChange={(ev) => setConstancia(ev.target.value)}
              placeholder="El número de la constancia que entregó el cliente"
              hint={f.retConstancia ? undefined : 'Sin la constancia, lo retenido no se puede acreditar.'}
            />
          </div>
          {constancia != null && constancia.trim() !== (f.retConstancia ?? '') && (
            <Button size="sm" onClick={() => anotar.mutate()} disabled={anotar.isPending}>
              Anotar
            </Button>
          )}
        </div>
      )}

      <p className="text-xs text-slate-500">
        De la venta{' '}
        <EntityLink tipo="venta" id={f.sale.id} etiqueta={f.sale.numero} mono sinIcono />. Para
        guardarla como PDF: «Imprimir / PDF» y, en el destino, «Guardar como PDF».
      </p>
    </div>
  );
}

function Renglon({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between text-slate-600 dark:text-slate-300">
      <span>{etiqueta}</span>
      <span className="tabular-nums">{valor}</span>
    </div>
  );
}
